import {
  AppError,
  ErrorHandler,
  ErrorHandlingResult,
  RecoveryAction
} from '../types/error';
import {
  NetworkErrorHandler,
  AuthenticationErrorHandler,
  AuthorizationErrorHandler,
  ValidationErrorHandler,
  StorageErrorHandler,
  DatabaseErrorHandler,
  BusinessLogicErrorHandler,
  ExternalApiErrorHandler,
  DefaultErrorHandler
} from './errorHandlers';
import { ErrorLogger } from './errorLogger';
import { ErrorFactory } from './errorFactory';

/**
 * Retry configuration
 */
interface RetryConfig {
  maxAttempts: number;
  baseDelay: number;
  maxDelay: number;
  exponentialBackoff: boolean;
  jitter: boolean;
}

/**
 * Main error management service that orchestrates error handling
 */
export class ErrorManager {
  private static handlers: ErrorHandler[] = [];
  private static isInitialized = false;
  private static retryOperations = new Map<string, number>();

  static initialize(): void {
    if (this.isInitialized) return;

    // Register error handlers in order of specificity
    this.handlers = [
      new NetworkErrorHandler(),
      new AuthenticationErrorHandler(),
      new AuthorizationErrorHandler(),
      new ValidationErrorHandler(),
      new StorageErrorHandler(),
      new DatabaseErrorHandler(),
      new BusinessLogicErrorHandler(),
      new ExternalApiErrorHandler(),
      new DefaultErrorHandler() // Must be last as it handles everything
    ];

    this.isInitialized = true;
  }

  /**
   * Handle an error with automatic retry logic
   */
  static async handleError(
    error: unknown,
    context?: {
      operation?: string;
      retryConfig?: Partial<RetryConfig>;
      userId?: string;
    }
  ): Promise<ErrorHandlingResult> {
    // Convert unknown error to AppError
    const appError = this.normalizeError(error, context);

    // Find appropriate handler
    const handler = this.findHandler(appError);
    if (!handler) {
      console.error('No handler found for error:', appError);
      return this.createDefaultResult(appError);
    }

    // Handle the error
    const result = await handler.handle(appError);

    // Log user interaction for analytics
    if (context?.operation) {
      ErrorLogger.logUserAction(
        appError.code,
        `error_handled_${context.operation}`,
        result.success ? 'automatic_resolution' : 'manual_intervention_required'
      );
    }

    return result;
  }

  /**
   * Execute operation with automatic retry on failure
   */
  static async withRetry<T>(
    operation: () => Promise<T>,
    config: Partial<RetryConfig> & { operationName: string }
  ): Promise<T> {
    const retryConfig: RetryConfig = {
      maxAttempts: 3,
      baseDelay: 1000,
      maxDelay: 10000,
      exponentialBackoff: true,
      jitter: true,
      ...config
    };

    const operationId = `${config.operationName}_${Date.now()}`;
    let lastError: AppError | null = null;

    for (let attempt = 1; attempt <= retryConfig.maxAttempts; attempt++) {
      try {
        const result = await operation();

        // Clear retry count on success
        this.retryOperations.delete(operationId);

        // Log successful retry if there were previous failures
        if (lastError) {
          ErrorLogger.logUserAction(
            lastError.code,
            'retry_successful',
            `succeeded_on_attempt_${attempt}`
          );
        }

        return result;
      } catch (error) {
        lastError = this.normalizeError(error, {
          operation: config.operationName,
          userId: (error as any)?.context?.userId
        });

        // Check if error is retryable
        if (!lastError.retryable || attempt >= retryConfig.maxAttempts) {
          // Log final failure
          ErrorLogger.logUserAction(
            lastError.code,
            'retry_exhausted',
            `failed_after_${attempt}_attempts`
          );
          throw lastError;
        }

        // Calculate delay for next attempt
        const delay = this.calculateRetryDelay(attempt, retryConfig);

        // Log retry attempt
        ErrorLogger.logUserAction(
          lastError.code,
          `retry_attempt_${attempt}`,
          `will_retry_in_${delay}ms`
        );

        // Update retry count
        this.retryOperations.set(operationId, attempt);

        // Wait before retrying
        await this.delay(delay);
      }
    }

    // This should never be reached, but throw the last error just in case
    throw new Error(lastError?.userMessage || 'Operation failed after retries');
  }

  /**
   * Execute operation with graceful degradation
   */
  static async withGracefulDegradation<T, F>(
    primaryOperation: () => Promise<T>,
    fallbackOperation: () => Promise<F>,
    config: {
      operationName: string;
      fallbackMessage?: string;
    }
  ): Promise<T | F> {
    try {
      return await primaryOperation();
    } catch (error) {
      const appError = this.normalizeError(error, {
        operation: config.operationName
      });

      // Log fallback usage
      ErrorLogger.logUserAction(
        appError.code,
        'graceful_degradation',
        `fallback_used_for_${config.operationName}`
      );

      console.warn(
        `Primary operation failed, using fallback for ${config.operationName}:`,
        config.fallbackMessage || appError.userMessage
      );

      return await fallbackOperation();
    }
  }

  /**
   * Normalize unknown error to AppError
   */
  private static normalizeError(
    error: unknown,
    context?: { operation?: string; userId?: string }
  ): AppError {
    if (ErrorFactory.isAppError(error)) {
      return error;
    }

    // Handle Supabase errors
    if (error && typeof error === 'object' && 'code' in error) {
      return ErrorFactory.fromSupabaseError(error, context?.operation, {
        userId: context?.userId
      });
    }

    // Handle network errors (fetch API)
    if (error instanceof TypeError && error.message.includes('fetch')) {
      return ErrorFactory.createNetworkError(
        'FETCH_ERROR',
        'Network request failed. Please check your connection.',
        error.message,
        undefined,
        undefined,
        undefined,
        false,
        { userId: context?.userId }
      );
    }

    // Default to unknown error
    return ErrorFactory.fromUnknown(
      error,
      'UNKNOWN_ERROR',
      'An unexpected error occurred',
      { userId: context?.userId }
    );
  }

  /**
   * Find the appropriate handler for an error
   */
  private static findHandler(error: AppError): ErrorHandler | null {
    return this.handlers.find(handler => handler.canHandle(error)) || null;
  }

  /**
   * Create a default error handling result
   */
  private static createDefaultResult(error: AppError): ErrorHandlingResult {
    return {
      success: false,
      message: error.userMessage,
      shouldRetry: error.retryable,
      retryAfter: 1000,
      recoveryActions: error.recoveryActions || [
        {
          type: 'retry',
          label: 'Try Again',
          description: 'Retry the operation',
          icon: 'bi-arrow-clockwise'
        }
      ]
    };
  }

  /**
   * Calculate delay for retry with exponential backoff and jitter
   */
  private static calculateRetryDelay(
    attempt: number,
    config: RetryConfig
  ): number {
    let delay = config.baseDelay;

    if (config.exponentialBackoff) {
      delay *= Math.pow(2, attempt - 1);
    }

    // Apply jitter (random variation) to prevent thundering herd
    if (config.jitter) {
      delay += Math.random() * delay * 0.1; // ±10% jitter
    }

    return Math.min(delay, config.maxDelay);
  }

  /**
   * Promise-based delay utility
   */
  private static delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Check if operation is currently being retried
   */
  static isRetrying(operationName: string): boolean {
    for (const [key] of this.retryOperations) {
      if (key.startsWith(operationName)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Get retry count for operation
   */
  static getRetryCount(operationName: string): number {
    for (const [key, count] of this.retryOperations) {
      if (key.startsWith(operationName)) {
        return count;
      }
    }
    return 0;
  }

  /**
   * Clear all retry operations (useful for testing)
   */
  static clearRetryOperations(): void {
    this.retryOperations.clear();
  }

  /**
   * Create user-friendly error message with recovery actions
   */
  static createUserErrorMessage(
    error: AppError,
    recoveryActions?: RecoveryAction[]
  ): {
    title: string;
    message: string;
    severity: string;
    actions: RecoveryAction[];
  } {
    let title = 'Error';

    switch (error.category) {
      case 'network':
        title = 'Connection Problem';
        break;
      case 'authentication':
        title = 'Authentication Required';
        break;
      case 'authorization':
        title = 'Access Denied';
        break;
      case 'validation':
        title = 'Invalid Input';
        break;
      case 'storage':
        title = 'File Operation Failed';
        break;
      case 'database':
        title = 'Data Operation Failed';
        break;
      case 'business_logic':
        title = 'Operation Not Allowed';
        break;
      case 'external_api':
        title = 'External Service Error';
        break;
      default:
        title = 'Unexpected Error';
    }

    return {
      title,
      message: error.userMessage,
      severity: error.severity,
      actions: recoveryActions || error.recoveryActions || []
    };
  }
}

// Initialize error manager
ErrorManager.initialize();
